import * as React from 'react';

import { Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { DataTable, TableVariants } from '@/components/data-table/data-table';
import InfiniteScroll from '@/components/ui/infinite-scroll';

import {
  DefaultTableEmptyState,
  DefaultTableErrorState,
  DefaultTableLoadingState,
  EmptyStateProps,
  ErrorStateProps,
  LoadingStateProps,
} from './data-table-states';

interface DataTableEndlessPaginatedProps<TData> {
  table: Table<TData>;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error?: string;
  renderEmptyState?: (props: EmptyStateProps) => React.ReactNode;
  renderErrorState?: (props: ErrorStateProps) => React.ReactNode;
  renderLoadingState?: (props: LoadingStateProps) => React.ReactNode;
  onRowClick?: (row: TData) => void;
  /** Detail-route href for a row — enables CMD/CTRL+click and middle-click "open in new tab". */
  getRowHref?: (row: TData) => string | undefined;
  getRowClassName?: (row: TData) => string | undefined;
  /** See `DataTable.shouldAnimateRowEnter`. */
  shouldAnimateRowEnter?: (row: TData) => boolean;
  stickyHeader?: boolean;
  fixedHeight?: boolean;
  variant?: TableVariants;
  className?: string | undefined;
  /** Unique table ID for enabling column resizing with localStorage persistence */
  tableId?: string;
  /** Callback when column resize ends - used to persist column widths */
  onResizeEnd?: () => void;
  /** Number of trailing columns that should share remaining space equally (default: 1) */
  fillLastColumns?: number;
}

enum LastRowState {
  LOADING = 'LOADING',
  EMPTY_LIST = 'EMPTY_LIST',
  ERROR = 'ERROR',
  NONE = 'NONE',
}

export const DataTableEndlessPaginated = <TData,>({
  table,
  isLoading,
  hasMore,
  onLoadMore,
  error,
  renderEmptyState,
  renderErrorState,
  renderLoadingState,
  onRowClick,
  getRowHref,
  getRowClassName,
  shouldAnimateRowEnter,
  stickyHeader = false,
  fixedHeight = false,
  variant,
  className,
  tableId,
  onResizeEnd,
  fillLastColumns,
}: DataTableEndlessPaginatedProps<TData>) => {
  const rows = table.getRowModel().rows;
  const colSpan = table.getAllColumns().length;
  const { t } = useTranslation('common');
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  // Set up IntersectionObserver for stickyHeader mode
  React.useEffect(() => {
    if (!fixedHeight) return;

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !isLoading && !error) {
          onLoadMore();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [fixedHeight, hasMore, isLoading, error, onLoadMore]);

  let state: LastRowState;
  if (isLoading && !error) {
    state = LastRowState.LOADING;
  } else if (rows.length === 0 && !error && !isLoading) {
    state = LastRowState.EMPTY_LIST;
  } else if (error) {
    state = LastRowState.ERROR;
  } else {
    state = LastRowState.NONE;
  }

  const renderContent = () => {
    const stateProps = { colSpan };

    switch (state) {
      case LastRowState.LOADING:
        return renderLoadingState ? (
          renderLoadingState({ colSpan })
        ) : (
          <DefaultTableLoadingState colSpan={colSpan} />
        );
      case LastRowState.EMPTY_LIST:
        return renderEmptyState ? (
          renderEmptyState(stateProps)
        ) : (
          <DefaultTableEmptyState {...stateProps} />
        );
      case LastRowState.ERROR: {
        const errorMessage = error || t('error.generic');
        return renderErrorState ? (
          renderErrorState({ colSpan, error: errorMessage, retryLoad: onLoadMore })
        ) : (
          <DefaultTableErrorState colSpan={colSpan} error={errorMessage} retryLoad={onLoadMore} />
        );
      }
      case LastRowState.NONE:
        return null;
    }
  };

  // For stickyHeader, include sentinel in lastRowChildren
  const lastRowContent = (
    <>
      {renderContent()}
      {fixedHeight && hasMore && !error && (
        <tr data-sentinel>
          <td colSpan={colSpan} className="h-1 p-0">
            <div ref={sentinelRef} className="h-1 w-full" />
          </td>
        </tr>
      )}
    </>
  );

  // For fixedHeight mode, render directly without InfiniteScroll wrapper with forced stickyHeader
  if (fixedHeight) {
    return (
      <DataTable
        table={table}
        lastRowChildren={lastRowContent}
        onRowClick={onRowClick}
        getRowHref={getRowHref}
        getRowClassName={getRowClassName}
        shouldAnimateRowEnter={shouldAnimateRowEnter}
        stickyHeader={true}
        variant={variant}
        className={className}
        scrollContainerRef={scrollContainerRef}
        tableId={tableId}
        onResizeEnd={onResizeEnd}
        fillLastColumns={fillLastColumns}
      />
    );
  }

  // For non-fixedHeight mode, use InfiniteScroll wrapper as before
  return (
    <InfiniteScroll isLoading={isLoading} hasMore={hasMore} next={onLoadMore} error={error}>
      <DataTable
        table={table}
        lastRowChildren={lastRowContent}
        onRowClick={onRowClick}
        getRowHref={getRowHref}
        getRowClassName={getRowClassName}
        shouldAnimateRowEnter={shouldAnimateRowEnter}
        stickyHeader={stickyHeader}
        variant={variant}
        className={className}
        tableId={tableId}
        onResizeEnd={onResizeEnd}
        fillLastColumns={fillLastColumns}
      />
    </InfiniteScroll>
  );
};

export type { DataTableEndlessPaginatedProps, EmptyStateProps, ErrorStateProps, LoadingStateProps };
